import { Capacitor } from '@capacitor/core';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

const HAPTIC_STYLES = new Set<HapticStyle>([
  'light',
  'medium',
  'heavy',
  'selection',
  'success',
  'warning',
  'error'
]);

/** Matches anything the app treats as pressable — kept in sync with `NativeGestures`. */
export const INTERACTIVE_SELECTOR =
  'button, [role="button"], [role="tab"], [role="switch"], a[href], .tap, input[type="checkbox"], input[type="radio"]';

/**
 * Single source of truth for haptics across the app. Both the explicit
 * `useHaptics()` calls in components and the global press layer
 * (see `NativeGestures`) funnel through here so we can:
 *
 *  - lazy-load the Capacitor plugin once,
 *  - no-op cleanly on web / unsupported devices,
 *  - fire on pointerdown for instant tactile feedback,
 *  - pick a weight tier per control (light / medium / heavy / selection),
 *  - guarantee exactly one buzz per press.
 */

// Generic (non-forced) taps within this window collapse into the previous buzz.
const TAP_DEDUPE_MS = 60;
// Same element re-fired within one press gesture (pointerdown → click handler).
const PRESS_TARGET_DEDUPE_MS = 450;
// `useHaptics()` calls that trail a global pointerdown on the same tick.
const RECENT_PRESS_COLLAPSE_MS = 120;

let lastHapticAt = 0;
let lastHapticTarget: Element | null = null;
let lastHapticStyle: HapticStyle | null = null;

export type TriggerHapticOpts = {
  /** Always play — used by the global press layer and intentional semantic buzzes. */
  force?: boolean;
  /** The press target — collapses pointerdown + click on the same control. */
  target?: Element;
  /** Collapse if another haptic fired in the last ~120ms (same press chain). */
  collapseRecent?: boolean;
};

export function findInteractiveTarget(from: Element | null): HTMLElement | null {
  return from?.closest?.(INTERACTIVE_SELECTOR) as HTMLElement | null;
}

export function isInteractiveDisabled(el: HTMLElement): boolean {
  return (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true';
}

export function isHapticSuppressed(el: HTMLElement): boolean {
  return !!el.closest('[data-no-haptic]');
}

/**
 * Pick a haptic weight from the control itself — no per-screen wiring needed.
 * Override any time with `data-haptic="heavy|medium|light|selection|…"` or the
 * `.haptic-*` marker classes in `index.css`.
 */
export function resolveHapticStyle(el: HTMLElement): HapticStyle {
  let node: HTMLElement | null = el;
  while (node) {
    const explicit = node.getAttribute('data-haptic');
    if (explicit && HAPTIC_STYLES.has(explicit as HapticStyle)) {
      return explicit as HapticStyle;
    }
    for (const cls of ['haptic-heavy', 'haptic-selection', 'haptic-medium', 'haptic-light'] as const) {
      if (node.classList.contains(cls)) {
        return cls.replace('haptic-', '') as HapticStyle;
      }
    }
    if (node !== el && node.matches(INTERACTIVE_SELECTOR)) break;
    node = node.parentElement;
  }

  const role = el.getAttribute('role');
  if (role === 'switch' || role === 'tab') return 'selection';

  if (el.closest('[data-segment], [role="tablist"], nav[aria-label="Main navigation"]')) {
    return 'selection';
  }

  const cls = typeof el.className === 'string' ? el.className : '';

  // Primary CTAs — filled gradients, accent/night fills, featured shadows.
  if (
    el.classList.contains('shadow-featured') ||
    el.classList.contains('shadow-dark') ||
    /\b(from-accent|bg-night|bg-accent\b|bg-\[linear-gradient|bg-gradient-to)/.test(cls)
  ) {
    return 'heavy';
  }

  // Card / tile rows — noticeable but not as heavy as a commit CTA.
  if (el.classList.contains('shadow-tile') || el.classList.contains('shadow-card')) {
    return 'medium';
  }

  // Small round steppers / icon chips.
  if (el.classList.contains('rounded-full') && el.tagName === 'BUTTON') {
    return 'selection';
  }

  // Text-only / chevron back controls.
  if (
    (el.tagName === 'BUTTON' || role === 'button') &&
    !/\b(bg-|shadow-|from-|to-)/.test(cls) &&
    (el.textContent?.trim().length ?? 0) < 36
  ) {
    return 'light';
  }

  if (el.tagName === 'BUTTON' || role === 'button') return 'medium';

  return 'light';
}

export async function triggerHaptic(
  style: HapticStyle = 'light',
  opts?: TriggerHapticOpts
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const now = Date.now();

  if (opts?.collapseRecent && now - lastHapticAt < RECENT_PRESS_COLLAPSE_MS) return;

  if (
    opts?.target &&
    opts.target === lastHapticTarget &&
    now - lastHapticAt < PRESS_TARGET_DEDUPE_MS &&
    style === lastHapticStyle
  ) {
    return;
  }

  // Compute the gate synchronously (before the async import) so ordering is by
  // call time, not promise-resolution time.
  if (!opts?.force && now - lastHapticAt < TAP_DEDUPE_MS) return;

  lastHapticAt = now;
  lastHapticTarget = opts?.target ?? null;
  lastHapticStyle = style;

  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    switch (style) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case 'selection':
        await Haptics.selectionStart();
        await Haptics.selectionChanged();
        await Haptics.selectionEnd();
        break;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        break;
      case 'warning':
        await Haptics.notification({ type: NotificationType.Warning });
        break;
      case 'error':
        await Haptics.notification({ type: NotificationType.Error });
        break;
    }
  } catch {
    // Plugin not present or platform doesn't support — silently ignore.
  }
}

/** Fire the resolved press haptic for a control — used by the global gesture layer. */
export function triggerPressHaptic(el: HTMLElement): void {
  void triggerHaptic(resolveHapticStyle(el), { force: true, target: el });
}
